import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin-ui";

// Mirrors the dataengine's /admin/api/tables response shape:
// { table, row_count, size_bytes, size_pretty, last_update }.
type TableStat = {
  table: string;
  row_count?: number | null;
  size_bytes?: number | null;
  size_pretty?: string | null;
  last_update?: string | null;
};

type TablesResponse = { tables?: TableStat[] } | TableStat[];

type PreviewRow = Record<string, unknown>;
type PreviewResponse = { rows?: PreviewRow[]; columns?: string[] } | PreviewRow[];

export function TablesPage() {
  const [tables, setTables] = useState<TableStat[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<TablesResponse>("/admin/dataengine/tables");
        const arr: TableStat[] = Array.isArray(data) ? data : (data.tables ?? []);
        if (!cancelled) setTables(arr);
      } catch (e: any) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <PageHeader
        kicker="Data engine"
        title="Tables"
        sub="Postgres tables backing the data engine. Preview the first 100 rows for any table."
      />

      {err && (
        <div style={{ margin: "0 26px 14px", background: "rgba(122,45,31,0.08)", color: "var(--neg)", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
          {err}
        </div>
      )}

      <div style={{ margin: "0 26px", border: "1px solid var(--rule)", borderRadius: 10, background: "var(--surface)", overflow: "hidden" }}>
        <div style={hdr}>
          <div>Table</div>
          <div style={{ textAlign: "right" }}>Rows</div>
          <div style={{ textAlign: "right" }}>Size</div>
          <div style={{ textAlign: "right" }}>Last update</div>
          <div style={{ textAlign: "right" }}>—</div>
        </div>
        {tables.map((t) => (
          <div key={t.table} className="ops-row" style={{ ...row, cursor: "default" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--ink)", fontWeight: 500 }}>{t.table}</div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>
              {t.row_count == null ? "—" : t.row_count.toLocaleString("en-SG")}
            </div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-3)" }}>
              {t.size_pretty ?? (t.size_bytes != null ? fmtBytes(t.size_bytes) : "—")}
            </div>
            <div style={{ textAlign: "right", fontSize: 11.5, color: "var(--ink-3)" }}>{fmtTime(t.last_update)}</div>
            <div style={{ textAlign: "right" }}>
              <button onClick={() => setSelected(t.table)} style={smallBtn}>Preview</button>
            </div>
          </div>
        ))}
        {tables.length === 0 && !err && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic" }}>Loading…</div>
        )}
      </div>

      {selected && (
        <PreviewDrawer table={selected} onClose={() => setSelected(null)} />
      )}
      <div style={{ height: 22 }} />
    </>
  );
}

function PreviewDrawer({ table, onClose }: { table: string; onClose: () => void }) {
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<PreviewResponse>(`/admin/dataengine/preview?table=${encodeURIComponent(table)}`);
        const arr: PreviewRow[] = Array.isArray(data) ? data : (data.rows ?? []);
        if (!cancelled) {
          setRows(arr);
          setErr(null);
        }
      } catch (e: any) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [table]);

  const cols = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      Object.keys(r).forEach((k) => set.add(k));
      if (set.size > 12) break;
    }
    return Array.from(set);
  }, [rows]);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(23,32,29,0.32)", zIndex: 50,
      display: "flex", justifyContent: "flex-end",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(1100px, 90vw)", height: "100%", background: "var(--bg)",
        boxShadow: "-8px 0 24px rgba(23,32,29,0.10)",
        display: "flex", flexDirection: "column",
      }}>
        <header style={{ padding: "14px 20px", borderBottom: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10.5, color: "var(--ink-4)", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>Preview</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--ink)" }}>{table}</div>
          </div>
          <button onClick={onClose} style={{
            border: "1px solid var(--rule)", background: "transparent", color: "var(--ink-2)",
            padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12,
          }}>Close</button>
        </header>

        <div style={{ flex: 1, overflow: "auto", padding: 0 }}>
          {loading && <div style={{ padding: 24, color: "var(--ink-3)", fontStyle: "italic" }}>Loading…</div>}
          {err && <div style={{ padding: 16, color: "var(--neg)" }}>{err}</div>}
          {!loading && !err && rows.length === 0 && (
            <div style={{ padding: 24, color: "var(--ink-3)", fontStyle: "italic" }}>Empty.</div>
          )}
          {rows.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-mono)", fontSize: 11.5 }}>
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th key={c} style={{
                      textAlign: "left", padding: "8px 10px",
                      borderBottom: "1px solid var(--rule)", color: "var(--ink-4)",
                      fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
                      background: "var(--surface-2)", position: "sticky", top: 0,
                    }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    {cols.map((c) => (
                      <td key={c} style={{ padding: "6px 10px", borderBottom: "1px solid var(--rule)", color: "var(--ink-2)", verticalAlign: "top", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {fmtCell(r[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtCell(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}
function fmtTime(s?: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" });
}

const hdr: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "1.5fr 110px 110px 160px 100px",
  gap: 12, padding: "10px 14px", color: "var(--ink-4)",
  fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
  borderBottom: "1px solid var(--rule)", background: "var(--surface-2)",
};
const row: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "1.5fr 110px 110px 160px 100px",
  gap: 12, padding: "10px 14px", alignItems: "center",
  borderBottom: "1px solid var(--rule)",
};
const smallBtn: React.CSSProperties = {
  padding: "4px 9px", borderRadius: 5, border: "1px solid var(--rule)",
  background: "var(--surface)", color: "var(--ink-2)", cursor: "pointer", fontSize: 11.5,
};
